import React from "react";
import { Stack, Button, Typography, Avatar, Box } from "@datanomik/commons-ui";
import { ShieldOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { AccountResponse } from "../../types/account.types";

const TRANSFER_MODE_LABELS: Record<string, string> = {
  PIX: "PIX",
  BANK_TRANSFER: "TED",
};

export const formatCurrency = (value: string): string => {
  const num = parseFloat(value);
  if (isNaN(num)) return "R$ 0,00";
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const TypeBadge: React.FC<{ mode: string }> = ({ mode }) => (
  <Box
    component="span"
    sx={{
      bgcolor: "primary.lighter",
      color: "primary.main",
      border: "1px solid",
      borderColor: "primary.light",
      fontSize: 10,
      fontWeight: 600,
      px: 1,
      py: 0.25,
      borderRadius: "4px",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    }}
  >
    {TRANSFER_MODE_LABELS[mode] ?? mode}
  </Box>
);

const AccountCard: React.FC<{ role: string; account: AccountResponse }> = ({ role, account }) => (
  <Stack
    flex={1}
    sx={{
      p: 1.75,
      bgcolor: "grey.50",
      borderRadius: "8px",
      border: "1px solid",
      borderColor: "grey.100",
      minWidth: 0,
    }}
  >
    <Typography
      fontSize={9.5}
      fontWeight={600}
      color="text.disabled"
      sx={{ textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}
    >
      {role}
    </Typography>
    <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
      <Avatar
        src={account.institution.logo}
        alt={account.institution.name}
        sx={{
          width: 28,
          height: 28,
          borderRadius: "6px",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "transparent",
          "& img": { objectFit: "contain" },
        }}
      />
      <Stack sx={{ minWidth: 0 }}>
        <Typography fontSize={12.5} fontWeight={500} noWrap>
          {account.owner.fiscalName}
        </Typography>
        <Typography fontSize={10.5} color="text.disabled" noWrap sx={{ fontVariantNumeric: "tabular-nums", mt: 0.125 }}>
          Ag. {account.account.branch} · CC: {account.account.accountNumber}
        </Typography>
      </Stack>
    </Stack>
  </Stack>
);

export { AccountCard };

interface DirectTransferConfirmStepProps {
  originAccount: AccountResponse | null;
  destinationAccount: AccountResponse | null;
  amount: string;
  description: string;
  transferMode: string;
  isSubmitting: boolean;
  onConfirm: () => void;
  onBack: () => void;
}

export const DirectTransferConfirmStep: React.FC<DirectTransferConfirmStepProps> = ({
  originAccount,
  destinationAccount,
  amount,
  description,
  transferMode,
  isSubmitting,
  onConfirm,
  onBack,
}) => {
  const { t } = useTranslation();

  return (
    <Stack>
      <Stack sx={{ px: 3, pt: 1, pb: 3 }}>
        <Stack
          alignItems="center"
          sx={{ py: 3, borderBottom: "1px solid", borderColor: "grey.100" }}
        >
          <Typography
            fontSize={10.5}
            fontWeight={500}
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75 }}
          >
            {t("Transfer amount")}
          </Typography>
          <Typography fontSize={38} fontWeight={700} letterSpacing="-0.02em" sx={{ lineHeight: 1 }}>
            {formatCurrency(amount)}
          </Typography>
          <Box sx={{ mt: 1.25 }}>
            <TypeBadge mode={transferMode} />
          </Box>
        </Stack>

        <Stack direction="row" gap={1.25} sx={{ py: 2.5 }}>
          {originAccount && <AccountCard role={t("Origin")} account={originAccount} />}
          {destinationAccount && <AccountCard role={t("Destination")} account={destinationAccount} />}
        </Stack>

        {description && (
          <Stack
            sx={{
              px: 1.75,
              py: 1.5,
              bgcolor: "grey.50",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "grey.100",
            }}
          >
            <Typography
              fontSize={10}
              fontWeight={600}
              color="text.disabled"
              sx={{ textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}
            >
              {t("Description")}
            </Typography>
            <Typography fontSize={13} color="text.secondary">
              {description}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" alignItems="center" justifyContent="center" gap={0.75} sx={{ mt: 1.75 }}>
          <ShieldOutlined sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography fontSize={11.5} color="text.disabled">
            {t("Protected and audited transfer")}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.25}
        sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.100", flexShrink: 0 }}
      >
        <Button variant="outlined" color="black" onClick={onBack} disabled={isSubmitting} sx={{ px: 2.5 }}>
          {t("Back")}
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={isSubmitting} sx={{ px: 3 }}>
          {t("Confirm")}
        </Button>
      </Stack>
    </Stack>
  );
};
